import React, { useEffect, useRef, useState } from "react";
import SlideOver from "../../Modal/SlideOver";
import { isBusinessWriteBlocked } from "../../../../Constants/business";
import WriteBlockGate from "../../Business/WriteBlockGate";
import { useTranslation } from "react-i18next";
import Button from "../../Button";
import { cancelStorageRequest, getAccountingFileName } from "../../../../Utils";
import { downloadData, getUrl } from "aws-amplify/storage";
import FormError from "../../Form/FormError";
import {
  requestDocumentExcelAccUpload,
  useAccountingDispatch,
  useAccountingState,
  useAuthState,
} from "../../../../Context";
import { useNavigate } from "react-router-dom";
import DownloadTemplateCard from "../../DownloadTemplateCard";
import UploadFile from "../../UploadFile";

const AccountUploadModal = ({ open, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useAccountingDispatch();
  const navigate = useNavigate();

  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [fieldErrorFile, setfieldErrorFile] = useState({
    status: false,
    message: "",
  });
  const [downloading, setDownloading] = useState(false);
  const [downloadDocError, setDownloadDocError] = useState(null);
  const fileInputRef = useRef(null);
  const downloadRef = useRef(null);

  const {
    uploadError,
    progressUpload,
    storageRequest,
    uploadLoading,
    uploadSuccess,
    uploadStatusError,
  } = useAccountingState();
  const { user } = useAuthState();
  const writeBlocked = isBusinessWriteBlocked(user?.mainBusiness);

  useEffect(() => {
    return () => {
      if (storageRequest) {
        cancelStorageRequest(storageRequest);
      }
    };
  }, [storageRequest]);

  useEffect(() => {
    if (uploadSuccess || uploadStatusError) {
      navigate(`/account/import?id=${user.mainBusiness?.id}`);
    }
  }, [uploadSuccess, user.mainBusiness?.id, uploadStatusError, navigate]);

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "download";
    const clickHandler = () => {
      setTimeout(() => {
        URL.revokeObjectURL(url);
        a.removeEventListener("click", clickHandler);
      }, 150);
    };
    a.addEventListener("click", clickHandler, false);
    a.click();
    return a;
  };

  const downloadFile = async (key) => {
    try {
      setDownloading(true);

      const request = downloadData({
        path: `public/${key}`,
      });

      downloadRef.current = request;
      const result = await request.result;

      const fileName = getAccountingFileName(key);

      downloadBlob(result.body, fileName);
    } catch (downloadError) {
      if (downloadError.message !== "canceled") {
        await handleDownloadError(key, downloadError);
      }
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadError = async (key) => {
    try {
      const urlResult = await getUrl({
        path: `public/${key}`,
        options: {
          useAccelerateEndpoint: true,
        },
      });
      const signedURL = urlResult.url.href;
      window.open(signedURL);
    } catch (signedURLError) {
      setDownloadDocError(signedURLError);
    }
  };

  const handleDownloadTemplate = () => {
    // Assuming you have a specific key for the template file
    downloadFile("template/accounting_account_template.xlsx");
  };

  const handleFileValidation = (file) => {
    const maxSize = 100 * 1024; // 100 KB
    const allowedType =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

    const validations = [
      {
        condition: file.type !== allowedType,
        message: "accounts.errors.fileType",
      },
      {
        condition: file.size > maxSize,
        message: "accounts.errors.fileSize",
      },
      {
        condition: file.name.length >= 120,
        message: "accounts.errors.fileCharacter",
      },
    ];

    const failedValidation = validations.find((val) => val.condition);

    return failedValidation
      ? { isValid: false, message: failedValidation.message }
      : { isValid: true, message: "" };
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.currentTarget.contains(e.relatedTarget)) {
      return;
    }
    setIsDragActive(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    const file = e.dataTransfer ? e.dataTransfer.files[0] : e.target.files[0];
    const validation = handleFileValidation(file);

    if (validation.isValid) {
      setfieldErrorFile({ status: false, message: "" });
      setSelectedFile(file);
    } else {
      setfieldErrorFile({ status: true, message: validation.message });
      e.target.value = "";
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    const validation = handleFileValidation(file);

    if (validation.isValid) {
      setfieldErrorFile({ status: false, message: "" });
      setSelectedFile(file);
    } else {
      setfieldErrorFile({ status: true, message: validation.message });
      e.target.value = "";
    }
  };

  const handleFileSelect = () => {
    fileInputRef.current.click();
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!selectedFile) {
      setfieldErrorFile({
        status: true,
        message: "accounts.errors.fileNone",
      });
      return;
    } else {
      requestDocumentExcelAccUpload(dispatch, {
        businessID: user.mainBusiness?.id,
        file: selectedFile,
      });
    }
  };

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title={"accounts.modal.title.upload"}
      footer={
        <div className="flex justify-end space-x-3">
          <Button type="button" variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            loading={uploadLoading}
            disabled={writeBlocked || uploadLoading}
            form="upload-form"
            variant="primary"
          >
            {t("common.continue")}
          </Button>
        </div>
      }
    >
      <form id="upload-form" onSubmit={handleSubmit}>
        <WriteBlockGate>
        <FormError className="mb-6" error={downloadDocError || uploadError} />
        <div className="flex flex-col space-y-6">
          <DownloadTemplateCard
            description="accounts.modal.description.template"
            onClick={handleDownloadTemplate}
            loading={downloading}
            disabled={downloading}
          />

          {/* Upload File */}
          <UploadFile
            title="accounts.action.upload"
            file={selectedFile}
            setFile={setSelectedFile}
            handleFileSelect={handleFileSelect}
            handleDragEnter={handleDragEnter}
            handleDragLeave={handleDragLeave}
            handleDragOver={handleDragOver}
            handleDrop={handleDrop}
            handleFileChange={handleFileChange}
            fileInputRef={fileInputRef}
            isDragActive={isDragActive}
            fieldErrorFile={fieldErrorFile}
            uploadLoading={uploadLoading}
            storageRequest={storageRequest}
            progressUpload={progressUpload}
          />
        </div>
        </WriteBlockGate>
      </form>
    </SlideOver>
  );
};

export default AccountUploadModal;
